import type { IContentTypeSetting } from '~/features-shared/content/interfaces/content'
import type { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ExperienceName, IExperience } from '~/features-shared/experience/interfaces/experience'
import type {
  FacetContentType,
  FacetExperience,
  FacetUnion,
} from '~/features-shared/facet/interfaces/facet'
import { FacetType } from '~/features-shared/facet/interfaces/facet'

export function isFacetExperience(facet: FacetUnion): facet is FacetExperience {
  return facet.type === FacetType.EXPERIENCE
}
export function isFacetContentType(facet: FacetUnion): facet is FacetContentType {
  return facet.type === FacetType.CONTENT_TYPE
}

/**
 *  Create and load facet styles
 * @param facet
 */
export function createFacetStyles(facet: FacetExperience): void {
  if (!facet.uri || !facet.color)
    return
  const css = `
    .bg-facet-${facet.uri} {
      background-color: ${facet.color};
    }
    .text-facet-${facet.uri} {
      color: ${facet.color};
    }
    .border-facet-${facet.uri} {
      border-color: ${facet.color};
    }
    .dropdown-menu .dropdown-menu-wrap .item .text-facet-${facet.uri} {
      color: ${facet.color};
    }
  `
  const { load } = useStyleTag(css, { id: facet.uri, immediate: true, manual: true })
  load()
}

/**
 *  convert community contentType settings to Facet
 * @param contentTypeSetting
 * @param position
 * @returns
 */
export function contentTypeSettingToFacet(contentTypeSetting: IContentTypeSetting, position: number): FacetContentType {
  const facet: FacetContentType = {
    tempUri: crypto.randomUUID(),

    type: FacetType.CONTENT_TYPE,
    model: contentTypeSetting.model,
    position,
    enabled: contentTypeSetting.isEnabled,
  }

  return facet
}

/**
 * Convert experiences to usable facet within swym
 *
 * @param experience
 * @param position
 * @returns
 */
export function experienceToFacet(experience: IExperience, position: number): FacetExperience {
  const facet: FacetExperience = {
    tempUri: crypto.randomUUID(),

    type: FacetType.EXPERIENCE,
    experience: experience.name,
    label: experience.label,
    icon: experience.icon,
    color: experience.color,
    serviceName: experience.serviceName,
    appId: experience.appId,
    position,
    enabled: false,
  }

  return facet
}

/**
 * Builds the list of facets based on content type settings and available default experiences.
 *
 * @note Only used when GET facets response returns an empty array.
 *
 * @param contentTypeSettings - The settings of content types.
 * @param availableDefaultExperiences - The default experiences available.
 */
export async function buildFacets(
  contentTypeSettings: IContentTypeSetting[],
  availableDefaultExperiences: IExperience[],
) {
  const facets: FacetUnion[] = []

  // Content Types
  contentTypeSettings.sort((contentTypeA, contentTypeB) => {
    if (contentTypeA.order < contentTypeB.order)
      return -1
    if (contentTypeA.order > contentTypeB.order)
      return 1
    return 0
  })
  const lastPosition = facets.push(...contentTypeSettings.map((contentTypeSettingValue, index) => {
    return contentTypeSettingToFacet(contentTypeSettingValue, index)
  }))

  // Experiences
  facets.push(...availableDefaultExperiences.map((defaultExperience, index) => {
    return experienceToFacet(defaultExperience, lastPosition + index)
  }))

  return facets
}

/**
 * Completes the existing experiences list with available default experiences.
 *
 * This method ensures that missing default experiences are added to the list of saved experiences from a community.
 * For example, if a community has GA facet experiences saved, this method can complete them with the corresponding LA facets.
 *
 * @param options - The options.
 * @param options.experiencesList - The list of experiences that already exist.
 * @param options.availableDefaultExperiences - The list of all available default experiences.
 * @param options.startPosition - The initial position from which new facets will be assigned.
 *
 * @returns The list of default facet experiences that were missing.
 */
function completeDefaultExperiences({
  experiencesList,
  availableDefaultExperiences,
  startPosition = 0,
}: {
  experiencesList: ExperienceName[]
  availableDefaultExperiences: IExperience[]
  startPosition: number
}): FacetExperience[] {
  const missingDefaultExperiences: IExperience[] = []

  const experiencesListSet = new Set(experiencesList)

  availableDefaultExperiences.forEach((availableDefaultExperience) => {
    if (!experiencesListSet.has(availableDefaultExperience.name))
      missingDefaultExperiences.push(availableDefaultExperience)
  })

  return missingDefaultExperiences.map((experience, index) =>
    experienceToFacet(
      experience,
      startPosition + index,
    ))
}

/**
 * Completes the existing content model list with available content type settings.
 *
 * This method ensures that any missing content types are added based on the community settings.
 * For example, if a community has all facet content types saved, this method can complete them with the conference content type.
 *
 * @param options - The options.
 * @param options.contentModelList - The list of content type model that already exist.
 * @param options.contentTypeSettings - The list of content types settings (from community settings)
 * @param options.startPosition - The initial position from which new facets will be assigned.
 *
 * @returns The list of facet content types that were missing.
 */
function completeContentTypes({
  contentModelList,
  contentTypeSettings,
  startPosition = 0,
}: {
  contentModelList: IContentModel[]
  contentTypeSettings: IContentTypeSetting[]
  startPosition: number
}): FacetContentType[] {
  const missingContentTypeSettings: IContentTypeSetting[] = []

  const contentModelListSet = new Set(contentModelList)

  contentTypeSettings.forEach((contentTypeSetting) => {
    if (!contentModelListSet.has(contentTypeSetting.model)) {
      missingContentTypeSettings.push(contentTypeSetting)
    }
  })

  return missingContentTypeSettings.map((contentTypeSetting, index) =>
    contentTypeSettingToFacet(
      contentTypeSetting,
      startPosition + index,
    ))
}

/**
 * Cleans the facets by removing obsolete experiences and content types.
 *
 * This function ensures that the list of facets remains up to date by:
 * - Removing facet content types that have been disabled in the settings.
 * - Removing facet experiences that are no longer valid.
 *
 * @param options - The options.
 * @param options.facets - The list of facets.
 * @param options.availableDefaultExperiences - The list of all available default experiences.
 * @param options.contentTypeSettings - - The list of content type settings (from community settings).
 * @returns The cleaned list of facets, with obsolete experiences and content types removed.
 */
function cleanFacets({
  facets,
  availableDefaultExperiences,
  contentTypeSettings,
}: {
  facets: FacetUnion[]
  availableDefaultExperiences: IExperience[]
  contentTypeSettings: IContentTypeSetting[]
}): FacetUnion[] {
  const contentTypeModelsFromSettings: IContentModel[] = contentTypeSettings.map(
    contentTypeSetting => contentTypeSetting.model,
  )
  const experienceTypesFromDefaultExperiences: ExperienceName[] = availableDefaultExperiences.map(
    availableDefaultExperience => availableDefaultExperience.name,
  )

  const cleanFacets: FacetUnion[] = [...facets].filter((facet) => {
    if (isFacetContentType(facet))
      return contentTypeModelsFromSettings.includes(facet.model)

    if (isFacetExperience(facet))
      return experienceTypesFromDefaultExperiences.includes(facet.experience)

    return false
  })

  // Reset all facets position
  cleanFacets.forEach((facet, index) => {
    facet.position = index
  })

  return cleanFacets
}

/**
 * Synchronizes the list of facets by ensuring it includes all necessary content types and experiences,
 * while also removing facets linked to disabled content types or deprecated experiences (GA/LA).
 *
 * Useful for ensuring that the list of facets includes all experiences and content types that might be missing.
 *
 * @param options - The options.
 * @param options.facets - The list of community facets.
 * @param options.availableDefaultExperiences - The list of all available default experiences.
 * @param options.contentTypeSettings - The list of all content type settings.
 *
 * @returns The updated list of facets after synchronization.
 */
export function synchronizeFacets({
  facets,
  availableDefaultExperiences,
  contentTypeSettings,
}: {
  facets: FacetUnion[]
  availableDefaultExperiences: IExperience[]
  contentTypeSettings: IContentTypeSetting[]
}) {
  const newFacets: FacetUnion[] = [
    ...facets,
  ]

  // Retrieve all experiences type from facets
  const experiencesFromFacets: ExperienceName[] = newFacets
    .filter(newFacet => isFacetExperience(newFacet))
    .map(newFacetExperience => newFacetExperience.experience)

  // Retrieve missing facet experiences
  const missingFacetExperiences: FacetExperience[] = completeDefaultExperiences({
    experiencesList: experiencesFromFacets,
    availableDefaultExperiences,
    startPosition: newFacets.length,
  })

  newFacets.push(...missingFacetExperiences)

  // Retrieve all content types models from facets
  const contentTypeModelsFromFacets: IContentModel[] = newFacets
    .filter(newFacet => isFacetContentType(newFacet))
    .map(newFacetContentType => newFacetContentType.model)

  // Retrieve missing facet content types
  const missingFacetContentTypes: FacetContentType[] = completeContentTypes({
    contentModelList: contentTypeModelsFromFacets,
    contentTypeSettings,
    startPosition: newFacets.length,
  })

  newFacets.push(...missingFacetContentTypes)

  // Clean facets
  return cleanFacets({
    facets: newFacets,
    availableDefaultExperiences,
    contentTypeSettings,
  })
}
